import { HTMLAttributes } from 'react';
import { Controller, Control, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  MarketField,
  MarketList,
  MarketRow,
  MarketSelect,
} from '@market/react';
import { DATADOG_PRIVACY_LEVELS } from 'constants/enums';

export const INPUT_SELECT_TEST_ID = 'input-select-test-id';

type ControlledInputSelectProps = HTMLAttributes<HTMLElement> & {
  // fields used by the controller
  control: Control;
  defaultValue?: string;
  name: string;
  rules?: RegisterOptions;
  shouldUnregister?: boolean;

  // fields used by Market components, there are more that are not listed here
  // visit go/marketstorybook to see all available components and their props
  disabled?: boolean;
  label?: string;
  options: { label?: string; value: string; displayValue?: string }[];

  // config options for datadog
  shouldMaskValues?: boolean;

  // the rules prop is not used if a custom resolver is provided
  // this will be set as false by default as most of our controlled inputs will be used in forms
  // if you are using this component outside of a form, set this to true to use the rules prop
  useRules?: boolean;
};

export const ControlledInputSelect = ({
  className,
  control,
  defaultValue = '',
  name,
  rules = {},
  shouldUnregister = false,
  disabled = false,
  label,
  options,
  shouldMaskValues = false,
  useRules = false,
}: ControlledInputSelectProps) => {
  const { i18n, t } = useTranslation();

  if (useRules) {
    // small hack because `required` doesn't doesn't do a whitespace check
    if (rules?.required) {
      // eslint-disable-next-line no-param-reassign
      rules.validate = (value) => Boolean(value.trim());
    }

    // this fun looking regex ensures that the value must be one of the options or an empty string if a value is not required
    // eslint-disable-next-line no-param-reassign
    rules.pattern = new RegExp(
      `${rules?.required ? '' : '^$|'}^(${options
        .map((o) => o.value)
        .join('|')})$`,
    );
  }

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      shouldUnregister={shouldUnregister}
      {...(useRules && { rules })}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
        formState: { isSubmitting },
      }) => (
        <MarketField
          className={className}
          disabled={disabled || isSubmitting}
          name={name}
          invalid={Boolean(error)}
        >
          <MarketSelect
            data-testid={INPUT_SELECT_TEST_ID}
            value={value}
            onMarketSelectClosed={onBlur}
            onMarketSelectValueDidChange={(e) => onChange(e.detail.value)}
            {...(shouldMaskValues && {
              'data-dd-privacy': DATADOG_PRIVACY_LEVELS.MASK,
            })}
          >
            <label>{label}</label>
            <MarketList slot="list">
              {options.map((option) => (
                <MarketRow
                  key={option.value}
                  value={option.value}
                  selected={value === option.value}
                  {...(shouldMaskValues && {
                    'data-dd-privacy': DATADOG_PRIVACY_LEVELS.MASK,
                  })}
                >
                  {option.displayValue || option.label}
                </MarketRow>
              ))}
            </MarketList>
          </MarketSelect>
          {error && (
            <small slot="error">
              {error?.message && i18n.exists(error.message)
                ? t(error.message, { label: label.toLowerCase() })
                : t('error.validation.invalidValue')}
            </small>
          )}
        </MarketField>
      )}
    />
  );
};

export default ControlledInputSelect;
